import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Loader2, ArrowLeft, Heart, ShieldCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ArticleDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/articles/${slug}`);
        setArticle(data);
      } catch (e) {
        setError(e?.response?.data?.detail || 'Article not found');
      }
    })();
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center text-center p-6">
        <div>
          <Heart className="w-8 h-8 text-[#7B2C3B] mx-auto" />
          <h1 className="font-serif text-3xl mt-3">{error}</h1>
          <Link to="/articles" className="mt-5 inline-block text-sm text-[#7B2C3B] hover:underline">Back to articles</Link>
        </div>
      </div>
    );
  }
  if (!article) {
    return <div className="min-h-screen bg-[#FBF7F2] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#7B2C3B]" /></div>;
  }

  // Lightweight markdown parsing: **bold** + paragraph breaks
  const renderBody = (line, i) => {
    const parts = line.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
    return (
      <p key={i} className="text-base text-[#3A3A3A] leading-relaxed mb-5">
        {parts.map((p, j) =>
          p.startsWith('**') && p.endsWith('**')
            ? <strong key={j} className="font-semibold text-[#2A2A2A]">{p.slice(2, -2)}</strong>
            : <span key={j}>{p}</span>
        )}
      </p>
    );
  };

  return (
    <div className="min-h-screen bg-[#FBF7F2]" data-testid="article-detail">
      <Navbar />
      <main className="px-6 lg:px-10 py-14">
        <article className="max-w-2xl mx-auto">
          <Link to="/articles" className="inline-flex items-center gap-1.5 text-xs text-[#7B2C3B] hover:underline">
            <ArrowLeft className="w-3.5 h-3.5" /> All articles
          </Link>
          <p className="section-eyebrow mt-6">{article.category} · {article.readTime}</p>
          <h1 className="font-serif text-4xl sm:text-5xl text-[#2A2A2A] mt-3 leading-tight">{article.title}</h1>
          <p className="text-lg text-[#6A6A6A] mt-4">{article.excerpt}</p>
          {article.cover && (
            <div className="mt-8 aspect-[16/9] rounded-3xl overflow-hidden">
              <img src={article.cover} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="mt-10">
            {article.body.map(renderBody)}
          </div>
          <div className="mt-12 p-6 rounded-3xl bg-white border border-[#EFE3D8] text-center">
            <ShieldCheck className="w-5 h-5 text-[#7B2C3B] mx-auto" />
            <p className="font-serif text-2xl mt-2">Plan calmly. Live well.</p>
            <p className="text-sm text-[#6A6A6A] mt-1">Build your wedding workspace in 60 seconds. Free to start.</p>
            <Link to="/auth" className="mt-4 inline-flex items-center gap-2 btn-primary px-5 py-2.5 rounded-full text-sm">
              Try it free <Heart className="w-3.5 h-3.5 fill-[#FBF7F2]" />
            </Link>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}
